import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select } from "@/components/ui/form";
import { CalendarClock, Car, CheckCircle2, Droplets, MapPin, ShieldCheck } from "lucide-react";

type ManagedBooking = {
  code: string;
  status: string;
  version: number;
  startAt: string;
  endAt: string;
  timezone: string;
  serviceMode: "STATIONARY" | "MOBILE";
  location: { name: string };
  resource: { name: string };
  services: Array<{ name: string; priceCents: number; quantity: number }>;
  addons: Array<{ name: string; priceCents: number; quantity: number }>;
  vehicle: { plate: string; make: string | null; model: string | null } | null;
  total: { amount: number; currency: string; unit: "minor" };
  permissions: { canCancel: boolean; canReschedule: boolean };
};

type CancellationPreview = {
  feeCents: number;
  withinWindow: boolean;
  hoursUntilStart: number;
  enabled: boolean;
  currency: string;
};

type Slot = { startAt: string; endAt: string };

export function ManageBookingPage() {
  const { code = "" } = useParams();
  const [token] = useState(() => extractToken(code));
  const [booking, setBooking] = useState<ManagedBooking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<CancellationPreview | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [startAt, setStartAt] = useState("");
  const [saving, setSaving] = useState(false);

  async function refresh(conflictMessage?: string) {
    if (!token) {
      setError("This management link is missing or has expired. Please use the link from your confirmation message.");
      setLoading(false);
      return;
    }
    try {
      const next = await managedRequest<ManagedBooking>(code, token, "");
      setBooking(next);
      setDate(localDate(next.startAt, next.timezone));
      setError(conflictMessage ?? null);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, [code, token]);

  useEffect(() => {
    if (!rescheduling || !date || !token) return;
    setStartAt("");
    setSlots([]);
    setError(null);
    const controller = new AbortController();
    managedRequest<Slot[]>(code, token, `/availability?date=${encodeURIComponent(date)}`, "GET", undefined, controller.signal)
      .then(setSlots)
      .catch((err) => {
        if ((err as Error).name !== "AbortError") setError(messageFor(err));
      });
    return () => controller.abort();
  }, [code, date, rescheduling, token]);

  async function showCancellationPreview() {
    setError(null);
    setNotice(null);
    try {
      setPreview(await managedRequest<CancellationPreview>(code, token, "/cancel-preview"));
    } catch (err) {
      await handleActionError(err);
    }
  }

  async function confirmCancellation() {
    if (!booking) return;
    setCancelling(true);
    setError(null);
    try {
      const updated = await managedRequest<ManagedBooking>(code, token, "/cancel", "POST", { version: booking.version });
      setBooking(updated);
      setPreview(null);
      setNotice("Your booking has been cancelled.");
    } catch (err) {
      await handleActionError(err);
    } finally {
      setCancelling(false);
    }
  }

  async function confirmReschedule() {
    if (!booking || !startAt) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await managedRequest<ManagedBooking>(code, token, "/reschedule", "POST", {
        startAt,
        version: booking.version,
      });
      setBooking(updated);
      setRescheduling(false);
      setNotice("Your booking time has been updated.");
    } catch (err) {
      await handleActionError(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleActionError(err: unknown) {
    if ((err as ManagedError).status === 409) {
      setPreview(null);
      setRescheduling(false);
      await refresh("This booking changed while you were viewing it. We refreshed the latest details; please try again.");
      return;
    }
    setError(messageFor(err));
  }

  if (loading) return <PageFrame><p className="py-20 text-center text-sm text-muted-foreground">Loading your booking...</p></PageFrame>;
  if (!booking) return <PageFrame><MessageCard title="We could not open this booking" message={error ?? "Please check your management link."} /></PageFrame>;

  const when = formatDateTime(booking.startAt, booking.timezone);
  return (
    <PageFrame>
      <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">Booking {booking.code}</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-[-0.035em]">Manage your appointment</h1>
        </div>
        <span className="w-fit rounded-full border bg-white px-3 py-1 text-xs font-semibold capitalize shadow-sm">{booking.status.toLowerCase().replace("_", " ")}</span>
      </div>

      {error && <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {notice && <div className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"><CheckCircle2 className="h-4 w-4" />{notice}</div>}

      <div className="grid gap-5 lg:grid-cols-[1.4fr_0.8fr]">
        <div className="space-y-5">
          <Card>
            <CardHeader><CardTitle className="text-base">Appointment details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Detail icon={<CalendarClock />} label="Date and time" value={when} />
              <Detail icon={<MapPin />} label="Location" value={`${booking.location.name} · ${booking.serviceMode === "MOBILE" ? "Mobile service" : booking.resource.name}`} />
              {booking.vehicle && <Detail icon={<Car />} label="Vehicle" value={[booking.vehicle.make, booking.vehicle.model, booking.vehicle.plate].filter(Boolean).join(" · ")} />}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Services</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {[...booking.services, ...booking.addons].map((item, index) => (
                <div className="flex justify-between gap-4" key={`${item.name}-${index}`}>
                  <span>{item.name}{item.quantity > 1 ? ` × ${item.quantity}` : ""}</span>
                  <span className="tabular-nums text-muted-foreground">{formatMoney(item.priceCents * item.quantity, booking.total.currency)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-3 text-base font-semibold">
                <span>Total</span><span className="tabular-nums">{formatMoney(booking.total.amount, booking.total.currency)}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit">
          <CardHeader><CardTitle className="text-base">Need to make a change?</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!booking.permissions.canCancel && !booking.permissions.canReschedule && (
              <p className="text-sm leading-6 text-muted-foreground">This booking can no longer be changed online. Please contact the location if you need help.</p>
            )}

            {booking.permissions.canReschedule && !rescheduling && !preview && (
              <Button className="w-full" onClick={() => setRescheduling(true)}>Choose another time</Button>
            )}
            {rescheduling && (
              <div className="space-y-3 rounded-xl border bg-muted/25 p-3">
                <div><Label>New date</Label><Input type="date" min={new Date().toISOString().slice(0, 10)} value={date} onChange={(event) => setDate(event.target.value)} /></div>
                <div><Label>Available time</Label><Select value={startAt} onChange={(event) => setStartAt(event.target.value)}><option value="">Choose a time</option>{slots.map((slot) => <option key={slot.startAt} value={slot.startAt}>{formatTime(slot.startAt, booking.timezone)}</option>)}</Select></div>
                {slots.length === 0 && <p className="text-xs text-muted-foreground">No available times on this date.</p>}
                <div className="flex gap-2"><Button className="flex-1" disabled={!startAt || saving} onClick={confirmReschedule}>{saving ? "Updating..." : "Confirm new time"}</Button><Button variant="outline" onClick={() => setRescheduling(false)}>Back</Button></div>
              </div>
            )}

            {booking.permissions.canCancel && !preview && !rescheduling && (
              <Button className="w-full" variant="outline" onClick={showCancellationPreview}>Cancel booking</Button>
            )}
            {preview && (
              <div className="space-y-3 rounded-xl border border-destructive/25 bg-destructive/[0.03] p-3">
                <p className="text-sm font-medium">Confirm cancellation</p>
                <p className="text-sm leading-6 text-muted-foreground">{preview.feeCents > 0 ? `A cancellation fee of ${formatMoney(preview.feeCents, preview.currency)} applies if you cancel now.` : "There is no cancellation fee if you cancel now."}</p>
                <p className="text-xs text-muted-foreground">This action cannot be undone.</p>
                <div className="flex gap-2"><Button className="flex-1" variant="destructive" disabled={cancelling} onClick={confirmCancellation}>{cancelling ? "Cancelling..." : "Yes, cancel"}</Button><Button variant="outline" onClick={() => setPreview(null)}>Keep booking</Button></div>
              </div>
            )}
            <p className="flex items-start gap-2 pt-2 text-xs leading-5 text-muted-foreground"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />This private link gives access to your booking. Do not share it.</p>
          </CardContent>
        </Card>
      </div>
    </PageFrame>
  );
}

function extractToken(code: string): string {
  const key = `shinebright:manage:${code}`;
  const fragmentToken = new URLSearchParams(window.location.hash.slice(1)).get("token");
  if (fragmentToken) {
    sessionStorage.setItem(key, fragmentToken);
    history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    return fragmentToken;
  }
  return sessionStorage.getItem(key) ?? "";
}

type ManagedError = Error & { status?: number; code?: string };

async function managedRequest<T>(code: string, token: string, suffix: string, method = "GET", body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`/api/public/v1/bookings/${encodeURIComponent(code)}${suffix}`, {
    method,
    headers: { Accept: "application/json", Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
    credentials: "same-origin",
    signal,
  });
  const payload = await response.json() as { data?: T; error?: { code?: string; message?: string } };
  if (!response.ok) {
    const error = new Error(payload.error?.message ?? "We could not complete that request.") as ManagedError;
    error.status = response.status;
    error.code = payload.error?.code;
    throw error;
  }
  return payload.data as T;
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return <div className="min-h-svh bg-background"><header className="border-b border-border/75 bg-white"><div className="mx-auto flex h-16 max-w-6xl items-center px-4 md:px-6"><div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-white"><Droplets className="h-[18px] w-[18px]" /></span><div><p className="text-base font-bold tracking-[-0.025em]">Shinebright</p><p className="text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Premium car care</p></div></div></div></header><main className="mx-auto max-w-5xl px-4 py-8 md:px-6 md:py-12">{children}</main></div>;
}

function MessageCard({ title, message }: { title: string; message: string }) {
  return <Card className="mx-auto mt-12 max-w-lg text-center"><CardHeader><CardTitle>{title}</CardTitle></CardHeader><CardContent><p className="text-sm leading-6 text-muted-foreground">{message}</p></CardContent></Card>;
}

function Detail({ icon, label, value }: { icon: React.ReactElement; label: string; value: string }) {
  return <div className="flex gap-3 [&_svg]:h-5 [&_svg]:w-5 [&_svg]:text-primary"><span className="mt-0.5">{icon}</span><div><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p><p className="mt-1 text-sm font-medium">{value}</p></div></div>;
}

function formatMoney(minor: number, currency: string) {
  const amount = minor / (new Set(["JPY", "KRW", "VND", "CLP"]).has(currency) ? 1 : 100);
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount); }
  catch { return `${currency} ${amount.toFixed(2)}`; }
}

function formatDateTime(value: string, timezone: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "full", timeStyle: "short", timeZone: timezone }).format(new Date(value));
}

function formatTime(value: string, timezone: string) {
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", timeZone: timezone }).format(new Date(value));
}

function localDate(value: string, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: timezone }).formatToParts(new Date(value));
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function messageFor(err: unknown) {
  const status = (err as ManagedError).status;
  if (status === 401) return "This management link is invalid or has expired. Please use the link from your confirmation message.";
  if (status === 429) return "Too many requests. Please wait a minute and try again.";
  return err instanceof Error ? err.message : "We could not complete that request.";
}
